import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Mail, MailOpen, MousePointerClick, TrendingUp, RefreshCw, Send, Loader2, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, tone = 'slate', testId }) => {
  const toneMap = {
    slate:    'text-slate-700',
    blue:     'text-blue-700',
    emerald:  'text-emerald-700',
    amber:    'text-amber-700',
    fuchsia:  'text-fuchsia-700',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className={`text-2xl font-bold mt-1 ${toneMap[tone]}`} data-testid={testId}>
          {value}
        </div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const VariantRow = ({ v, bandit, activeMetric, isWinner }) => {
  const pickPct = bandit ? Math.round((bandit.pick_probability ?? 0) * 100) : null;
  const postCtr = bandit ? (bandit.posterior_mean_ctr * 100).toFixed(1) : null;
  const postCvr = bandit ? (bandit.posterior_mean_cvr * 100).toFixed(1) : null;
  return (
  <tr className="hover:bg-slate-50" data-testid={`variant-row-${v.variant}`}>
    <td className="px-3 py-3 font-mono text-sm">
      <Badge
        variant="outline"
        className={
          v.variant === 'A' ? 'border-blue-400 text-blue-700 bg-blue-50'
          : v.variant === 'B' ? 'border-fuchsia-400 text-fuchsia-700 bg-fuchsia-50'
          : 'border-emerald-400 text-emerald-700 bg-emerald-50'
        }
      >
        {v.variant}
      </Badge>
      {isWinner && (
        <span className="ml-1 text-[10px] font-semibold text-amber-600" title="Best on active metric">
          👑
        </span>
      )}
    </td>
    <td className="px-3 py-3 text-sm text-slate-700 min-w-[240px]" title={v.subject}>
      "{v.subject}"
    </td>
    <td className="px-3 py-3 text-right font-mono">{v.sent}</td>
    <td className="px-3 py-3 text-right font-mono">{v.opened}</td>
    <td className="px-3 py-3 text-right font-mono">{v.clicked}</td>
    <td className="px-3 py-3 text-right font-mono text-emerald-700">{v.converted ?? 0}</td>
    <td className="px-3 py-3 text-right">
      <span
        className={`font-semibold ${v.click_rate >= 10 ? 'text-emerald-700' : v.click_rate >= 3 ? 'text-amber-600' : 'text-slate-500'}`}
      >
        {v.click_rate}%
      </span>
    </td>
    <td className="px-3 py-3 text-right">
      <span
        className={`font-semibold ${(v.conversion_rate ?? 0) >= 8 ? 'text-emerald-700' : (v.conversion_rate ?? 0) >= 3 ? 'text-amber-600' : 'text-slate-500'}`}
      >
        {v.conversion_rate ?? 0}%
      </span>
    </td>
    <td className="px-3 py-3 text-right" data-testid={`bandit-share-${v.variant}`}>
      {pickPct !== null ? (
        <div className="flex flex-col items-end gap-0.5">
          <span
            className={`text-sm font-semibold ${
              pickPct >= 60 ? 'text-emerald-700'
              : pickPct >= 30 ? 'text-blue-700'
              : 'text-slate-500'
            }`}
          >
            {pickPct}%
          </span>
          <div className="w-16 h-1.5 bg-slate-200 rounded overflow-hidden">
            <div
              className={`h-full ${
                pickPct >= 60 ? 'bg-emerald-500'
                : pickPct >= 30 ? 'bg-blue-500'
                : 'bg-slate-400'
              }`}
              style={{ width: `${Math.max(2, pickPct)}%` }}
            />
          </div>
          <span className="text-[9px] text-slate-400">
            {activeMetric === 'conversion'
              ? `posterior CVR ${postCvr}%`
              : `posterior CTR ${postCtr}%`}
          </span>
        </div>
      ) : <span className="text-slate-300">-</span>}
    </td>
  </tr>
);
};

export default function AdminReminderAnalytics() {
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/trust/admin/reminders/analytics`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: { days },
        }),
        axios.get(`${API}/api/trust/admin/reminders/events`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: { limit: 20 },
        }),
      ]);
      setFunnel(analyticsRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
      else if (e.response?.status === 403) { toast.error('Admin only'); navigate('/'); }
      else toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days, navigate]);

  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    setSavingMode(true);
    try {
      await axios.put(
        `${API}/api/trust/admin/reminders/bandit-config`,
        { mode, min_conversions_to_switch: 10 },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Bandit mode → ${mode}`);
      await load();
    } catch {
      toast.error('Failed to update bandit mode');
    } finally { setSavingMode(false); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(
        `${API}/api/trust/admin/reminders/backfill-conversions`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Scanned ${res.data.scanned}, converted ${res.data.converted}`);
      await load();
    } catch {
      toast.error('Backfill failed');
    } finally { setBackfilling(false); }
  };

  const runBatch = async (dry_run) => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/trust/admin/reminders/run`,
        { days: 7, limit: 200, dry_run },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      const d = res.data;
      if (dry_run) {
        toast.info(`Preview: ${d.candidates} sellers would receive the email`);
      } else {
        toast.success(`Sent to ${d.sent} sellers · ${d.failed_count} failed`);
      }
      await load();
    } catch {
      toast.error('Batch run failed');
    } finally { setSending(false); }
  };

  if (loading && !funnel) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading analytics…
      </div>
    );
  }

  const t = funnel?.totals || {};
  const rawVariants = funnel?.variants || [];
  const bandit = funnel?.bandit?.variants || [];
  const activeMetric = funnel?.bandit?.active_metric || 'click';
  const modeSetting = funnel?.bandit?.mode_setting || 'auto';
  const banditThreshold = funnel?.bandit?.conversion_threshold ?? 10;
  const conversionsInWindow = funnel?.bandit?.conversions_in_window ?? 0;
  const autoSwitchReady = funnel?.bandit?.auto_switch_ready ?? false;
  const allSubjects = funnel?.subject_variants || bandit.map(b => ({ variant: b.variant, subject: b.subject }));
  // Show ALL known variants (even ones with 0 sends) so the bandit share is visible
  const variants = allSubjects.map((s) => {
    const row = rawVariants.find((v) => v.variant === s.variant);
    return row || {
      variant: s.variant, subject: s.subject,
      sent: 0, opened: 0, clicked: 0, converted: 0,
      open_rate: 0, click_rate: 0, click_through_rate: 0, conversion_rate: 0,
    };
  });
  // Winner selection depends on the active metric - CTR when clicks, CVR when conversions
  const winner = rawVariants.reduce((best, v) => {
    if (v.sent < 5) return best;
    const key = activeMetric === 'conversion' ? 'conversion_rate' : 'click_rate';
    const bestKey = activeMetric === 'conversion' ? 'conversion_rate' : 'click_rate';
    return (!best || (v[key] ?? 0) > (best[bestKey] ?? 0)) ? v : best;
  }, null);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="reminder-analytics">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="analytics-title">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            Verify Reminder Analytics
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Open, click and verification conversion for the 7-day nudge email.
            The subject line with the highest click rate is your current winner.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            data-testid="analytics-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="analytics-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => runBatch(true)}
            disabled={sending}
            data-testid="analytics-preview-batch"
          >
            Preview batch
          </Button>
          <Button
            size="sm"
            onClick={() => runBatch(false)}
            disabled={sending}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="analytics-run-batch"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Run batch now
          </Button>
        </div>
      </div>

      {/* Top funnel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Mail} label="Sent" value={t.sent ?? 0} tone="slate" testId="stat-sent" />
        <StatCard icon={MailOpen} label="Opened" value={t.opened ?? 0} sub={`${t.open_rate ?? 0}% open rate`} tone="blue" testId="stat-opened" />
        <StatCard icon={MousePointerClick} label="Clicked" value={t.clicked ?? 0} sub={`${t.click_rate ?? 0}% click rate`} tone="fuchsia" testId="stat-clicked" />
        <StatCard icon={TrendingUp} label="Verified after email" value={t.converted_to_verified ?? 0} sub={`${t.verify_rate_of_openers ?? 0}% of openers`} tone="emerald" testId="stat-converted" />
        <StatCard icon={ShieldCheck} label="Window" value={`${funnel?.window_days ?? days}d`} tone="slate" />
      </div>

      {/* Variant table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span>A/B subject variants · Thompson-sampling bandit</span>
            <div className="flex items-center gap-2">
              {activeMetric && (
                <Badge
                  className={
                    activeMetric === 'conversion'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-blue-600 text-white'
                  }
                  data-testid="active-metric-badge"
                >
                  {activeMetric === 'conversion' ? '🎯 Optimising conversions' : '🖱️ Optimising clicks'}
                </Badge>
              )}
              {winner && (
                <Badge className="bg-amber-500 text-white">
                  👑 {activeMetric === 'conversion'
                    ? `Winner: ${winner.variant} - ${winner.conversion_rate ?? 0}% CVR`
                    : `Winner: ${winner.variant} - ${winner.click_rate}% CTR`}
                </Badge>
              )}
            </div>
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Traffic auto-allocated by a Beta-Bernoulli bandit. In <b>auto</b> mode it optimises for
            clicks until {banditThreshold} conversions accumulate, then switches to optimise verify-conversion.
            Currently: <b>{modeSetting}</b> · {conversionsInWindow} conversions in window
            {modeSetting === 'auto' && !autoSwitchReady &&
              ` · ${banditThreshold - conversionsInWindow} more needed to switch to conversion mode`}
          </p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs text-slate-500">Mode:</span>
            {['auto', 'click', 'conversion'].map((m) => (
              <Button
                key={m}
                size="sm"
                variant={modeSetting === m ? 'default' : 'outline'}
                onClick={() => setMode(m)}
                disabled={savingMode}
                data-testid={`mode-${m}`}
                className="h-7 text-xs px-3"
              >
                {m}
              </Button>
            ))}
            <span className="ml-4 text-xs text-slate-500">·</span>
            <Button
              size="sm"
              variant="outline"
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="backfill-conversions"
              className="h-7 text-xs px-3"
            >
              {backfilling && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Backfill conversions
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide border-b bg-slate-50">
                <th className="px-3 py-2 text-left">Variant</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Opened</th>
                <th className="px-3 py-2 text-right">Clicked</th>
                <th className="px-3 py-2 text-right">Converted</th>
                <th className="px-3 py-2 text-right">Click %</th>
                <th className="px-3 py-2 text-right">Conv %</th>
                <th className="px-3 py-2 text-right" title="Thompson-sampling probability this variant is picked on the next send">Traffic Share</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {variants.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    No sends in this window yet. Hit <b>Run batch now</b> to start collecting data.
                  </td>
                </tr>
              ) : variants.map((v) => (
                <VariantRow
                  key={v.variant}
                  v={v}
                  bandit={bandit.find((b) => b.variant === v.variant)}
                  activeMetric={activeMetric}
                  isWinner={winner && v.variant === winner.variant}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Recent events */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent sends (audit log)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide border-b bg-slate-50">
                <th className="px-3 py-2 text-left">Recipient</th>
                <th className="px-3 py-2 text-left">Variant</th>
                <th className="px-3 py-2 text-left">Provider</th>
                <th className="px-3 py-2 text-left">Sent</th>
                <th className="px-3 py-2 text-left">Opened</th>
                <th className="px-3 py-2 text-left">Clicked</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No events yet.</td></tr>
              ) : events.map((ev) => (
                <tr key={ev.id} className="hover:bg-slate-50" data-testid={`event-row-${ev.id}`}>
                  <td className="px-3 py-2 text-slate-800">{ev.email}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{ev.variant}</Badge></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{ev.provider}</td>
                  <td className="px-3 py-2 text-xs">
                    {ev.sent_at ? new Date(ev.sent_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {ev.opened_at ? (
                      <span className="text-emerald-700">
                        ✓ {new Date(ev.opened_at).toLocaleTimeString()}
                        {ev.open_count > 1 && <span className="text-slate-400"> · {ev.open_count}×</span>}
                      </span>
                    ) : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {ev.clicked_at ? (
                      <span className="text-fuchsia-700">
                        ✓ {new Date(ev.clicked_at).toLocaleTimeString()}
                        {ev.click_count > 1 && <span className="text-slate-400"> · {ev.click_count}×</span>}
                      </span>
                    ) : <span className="text-slate-400">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
